import { Injectable } from '@angular/core';
import {
  createAudioCleanupProcessor,
  type AudioCleanupParameters,
  type AudioCleanupProcessor,
} from '../../../core/render-runtime/shared/audio-cleanup';

interface PreviewCleanupChain {
  source: MediaElementAudioSourceNode;
  processorNode: ScriptProcessorNode;
  gainNode: GainNode;
  settingsKey: string;
  processor: AudioCleanupProcessor | null;
}

@Injectable({ providedIn: 'root' })
export class PreviewAudioCleanupService {
  private context: AudioContext | null = null;
  private readonly chains = new Map<HTMLMediaElement, PreviewCleanupChain>();

  apply(element: HTMLMediaElement, settings: AudioCleanupParameters | null | undefined, gain = 1): void {
    const chain = this.ensureChain(element);
    if (!chain) return;
    const settingsKey = JSON.stringify(settings ?? null);
    if (settingsKey !== chain.settingsKey) {
      this.configure(chain, settingsKey, settings);
    }
    chain.gainNode.gain.value = clampGain(gain);
    if (this.context?.state === 'suspended') void this.context.resume().catch(() => undefined);
  }

  reset(element: HTMLMediaElement, settings: AudioCleanupParameters | null | undefined, gain = 1): void {
    const chain = this.ensureChain(element);
    if (!chain) return;
    this.configure(chain, JSON.stringify(settings ?? null), settings);
    chain.gainNode.gain.value = clampGain(gain);
  }

  /**
   * Resume the shared preview graph synchronously from the user's Play action. Audio elements
   * routed through MediaElementAudioSourceNode stay silent while this context is suspended,
   * even when HTMLMediaElement.play() itself succeeds.
   */
  resume(): void {
    if (this.context?.state === 'suspended') {
      void this.context.resume().catch(() => undefined);
    }
  }

  hasChain(element: HTMLMediaElement): boolean {
    return this.chains.has(element);
  }

  disposeAll(): void {
    for (const chain of this.chains.values()) {
      chain.processorNode.onaudioprocess = null;
      chain.source.disconnect();
      chain.processorNode.disconnect();
      chain.gainNode.disconnect();
    }
    this.chains.clear();
    const context = this.context;
    this.context = null;
    if (context && context.state !== 'closed') void context.close().catch(() => undefined);
  }

  private ensureChain(element: HTMLMediaElement): PreviewCleanupChain | null {
    const existing = this.chains.get(element);
    if (existing) return existing;
    if (typeof AudioContext === 'undefined') return null;
    try {
      const context = this.context ?? new AudioContext({ latencyHint: 'interactive' });
      this.context = context;
      const source = context.createMediaElementSource(element);
      const processorNode = context.createScriptProcessor(1024, 2, 2);
      const gainNode = context.createGain();
      const chain: PreviewCleanupChain = {
        source,
        processorNode,
        gainNode,
        settingsKey: '',
        processor: null,
      };
      processorNode.onaudioprocess = event => this.process(chain, event);
      source.connect(processorNode);
      processorNode.connect(gainNode);
      gainNode.connect(context.destination);
      this.chains.set(element, chain);
      return chain;
    } catch {
      return null;
    }
  }

  private process(chain: PreviewCleanupChain, event: AudioProcessingEvent): void {
    const input = event.inputBuffer;
    const output = event.outputBuffer;
    const outputChannels = output.numberOfChannels;
    const inputChannels = input.numberOfChannels;
    const frame = new Float32Array(outputChannels);
    const inputs = Array.from({ length: inputChannels }, (_, channel) => input.getChannelData(channel));
    const outputs = Array.from({ length: outputChannels }, (_, channel) => output.getChannelData(channel));
    for (let index = 0; index < output.length; index += 1) {
      for (let channel = 0; channel < outputChannels; channel += 1) {
        const sourceChannel = Math.min(channel, Math.max(0, inputChannels - 1));
        frame[channel] = inputChannels ? inputs[sourceChannel][index] : 0;
      }
      chain.processor?.processFrame(frame);
      for (let channel = 0; channel < outputChannels; channel += 1) outputs[channel][index] = frame[channel];
    }
  }

  private configure(
    chain: PreviewCleanupChain,
    settingsKey: string,
    settings: AudioCleanupParameters | null | undefined,
  ): void {
    chain.settingsKey = settingsKey;
    chain.processor = createAudioCleanupProcessor(this.context!.sampleRate, 2, settings);
  }
}

function clampGain(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(2, value)) : 1;
}
